import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import AddUserDialog from "@/components/AddUserDialog";
import EditUserDialog from "@/components/EditUserDialog";
import { openAdminManual } from "@/lib/userManual";
import { openMarketingBrochure } from "@/lib/marketingBrochure";
import {
  ShieldCheck,
  Users,
  Briefcase,
  FileText,
  Video,
  DollarSign,
  UserPlus,
  MoreHorizontal,
  Pencil,
  Trash2,
  LogIn,
  BookOpen,
  Megaphone,
  ScanLine,
} from "lucide-react";

export default function AdminDashboard() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [recordingHealth, setRecordingHealth] = useState(null);
  const [scanQuality, setScanQuality] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    const [s, u, h, sq] = await Promise.all([
      api.get("/admin/stats"),
      api.get("/admin/users"),
      api.get("/admin/recording-health?hours=24").catch(() => ({ data: null })),
      api.get("/admin/scan-quality?hours=168").catch(() => ({ data: null })),
    ]);
    setStats(s.data);
    setUsers(u.data);
    setRecordingHealth(h.data);
    setScanQuality(sq.data);
  }, []);

  useEffect(() => {
    if (user === undefined) return;
    if (user === null) return;
    if (user.role !== "admin") {
      navigate("/dashboard");
      return;
    }
    load().finally(() => setLoading(false));
  }, [user, navigate, load]);

  const handleImpersonate = async (target) => {
    try {
      const { data } = await api.post(
        `/admin/users/${target.id}/impersonate`
      );
      // Stash the current admin JWT so we can switch back
      const currentToken = localStorage.getItem("aip_token");
      if (currentToken) {
        localStorage.setItem("aip_admin_token", currentToken);
        localStorage.setItem(
          "aip_impersonating",
          JSON.stringify({
            email: data.user.email,
            name: data.user.name,
            id: data.user.id,
          })
        );
      }
      localStorage.setItem("aip_token", data.token);
      toast.success(`Now logged in as ${data.user.email}`);
      await refresh();
      navigate("/dashboard");
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Failed to impersonate user"
      );
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/users/${deleteTarget.id}`);
      toast.success("User deleted");
      setDeleteTarget(null);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to delete user");
    } finally {
      setDeleting(false);
    }
  };

  if (loading || !stats) {
    return (
      <main className="mx-auto max-w-7xl px-6 py-12 text-slate-500">
        Loading admin…
      </main>
    );
  }

  const kpis = [
    { label: "MRR", value: `$${stats.mrr_usd.toFixed(0)}`, icon: DollarSign, bg: "bg-emerald-600" },
    { label: "Users", value: stats.users_count, icon: Users, bg: "bg-blue-700" },
    { label: "Paid users", value: stats.paid_users, icon: ShieldCheck, bg: "bg-violet-700" },
    { label: "Jobs", value: stats.jobs_count, icon: Briefcase, bg: "bg-slate-900" },
    { label: "Resumes", value: stats.resumes_count, icon: FileText, bg: "bg-amber-600" },
    { label: "Interviews", value: stats.interviews_total, icon: Video, bg: "bg-rose-600" },
  ];

  return (
    <main data-testid="admin-page" className="mx-auto max-w-7xl px-4 py-6 sm:px-6 sm:py-12">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-700">
            <ShieldCheck className="mr-1 inline h-3 w-3" /> Super-admin
          </div>
          <h1 className="mt-2 font-heading text-3xl font-semibold tracking-tight sm:text-4xl lg:text-5xl">
            Platform Overview
          </h1>
        </div>
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-black">
          {stats.admins_count} admin{stats.admins_count === 1 ? "" : "s"} ·{" "}
          {stats.employers_count} employer
          {stats.employers_count === 1 ? "" : "s"}
        </div>
      </div>

      <div className="mt-8 grid gap-px bg-slate-200 sm:grid-cols-3 lg:grid-cols-6">
        {kpis.map((k) => (
          <div
            key={k.label}
            data-testid={`admin-kpi-${k.label.toLowerCase().replace(/\s+/g, "-")}`}
            className={`${k.bg} p-5 text-white`}
          >
            <div className="flex items-center justify-between">
              <div className="text-[10px] font-bold uppercase tracking-[0.2em] opacity-80">
                {k.label}
              </div>
              <k.icon className="h-4 w-4 opacity-70" />
            </div>
            <div className="kpi-stat mt-3 font-heading text-3xl font-semibold">
              {k.value}
            </div>
          </div>
        ))}
      </div>

      {/* Recording pipeline health (last 24h) */}
      {recordingHealth && (
        <div
          data-testid="admin-recording-health"
          className="mt-6 border border-slate-200 bg-white"
        >
          <div className="flex items-center justify-between border-b border-slate-100 bg-slate-50 px-5 py-3">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-700">
              <Video className="mr-1 inline h-3 w-3" />
              Recording pipeline · last {recordingHealth.hours}h
            </div>
            <div className="flex items-center gap-2">
              {recordingHealth.backlog_rooms > 0 && (
                <span className="inline-flex items-center border border-rose-200 bg-rose-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-rose-700">
                  {recordingHealth.backlog_rooms} unfinalized
                </span>
              )}
              <button
                type="button"
                data-testid="admin-ops-alert-test-btn"
                onClick={async () => {
                  try {
                    const { data } = await api.post("/admin/ops-alert-test");
                    toast.success(
                      `Test alert sent to: ${(data.channels || []).join(", ")}`
                    );
                  } catch (e) {
                    toast.error(
                      e?.response?.data?.detail ||
                        "Could not send test alert"
                    );
                  }
                }}
                className="inline-flex items-center gap-1 border border-slate-300 bg-white px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-slate-700 hover:bg-slate-50"
                title="Send a test alert through Slack/Discord webhooks"
              >
                Test alert
              </button>
            </div>
          </div>
          <div className="grid gap-px bg-slate-100 sm:grid-cols-3 lg:grid-cols-6">
            {[
              { label: "Chunks", value: recordingHealth.chunks_uploaded, testid: "rh-chunks" },
              { label: "MB uploaded", value: recordingHealth.total_mb, testid: "rh-mb" },
              { label: "Rooms recorded", value: recordingHealth.distinct_rooms_in_window, testid: "rh-rooms" },
              { label: "Avg chunk", value: `${recordingHealth.avg_chunk_kb} KB`, testid: "rh-avg" },
              { label: "Finalized", value: recordingHealth.finalized_in_window, testid: "rh-final" },
              {
                label: "Oldest backlog",
                value:
                  recordingHealth.oldest_unfinalized_age_hours === null
                    ? "—"
                    : `${recordingHealth.oldest_unfinalized_age_hours}h`,
                testid: "rh-oldest",
              },
            ].map((m) => (
              <div
                key={m.label}
                data-testid={`admin-${m.testid}`}
                className="bg-white p-3"
              >
                <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                  {m.label}
                </div>
                <div className="mt-1 text-xl font-extrabold tabular-nums text-slate-900">
                  {m.value}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Scan-pipeline quality KPI (last 7d) */}
      {scanQuality && scanQuality.total_scans > 0 && (
        <div
          data-testid="admin-scan-quality"
          className="mt-6 border border-slate-200 bg-white"
        >
          <div className="flex items-center justify-between border-b border-slate-100 bg-slate-50 px-5 py-3">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-700">
              <ScanLine className="mr-1 inline h-3 w-3" />
              Scan pipeline quality · last {Math.round(scanQuality.hours / 24)}d
            </div>
            <div className="text-[10px] text-slate-500">
              Across résumé + employment-application scans
            </div>
          </div>
          <div className="grid gap-px bg-slate-100 sm:grid-cols-2 lg:grid-cols-4">
            {[
              {
                label: "Total scans",
                value: scanQuality.total_scans,
                hint:
                  `${scanQuality.by_source.scan_resume.total} résumé · ${scanQuality.by_source.scan_application.total} application`,
                testid: "sq-total",
              },
              {
                label: "Recruiter-edited",
                value: `${scanQuality.recruiter_edited_pct}%`,
                hint: `${scanQuality.recruiter_edited} of ${scanQuality.total_scans} needed manual fixes`,
                testid: "sq-edited",
                amber: scanQuality.recruiter_edited_pct >= 50,
                rose: scanQuality.recruiter_edited_pct >= 70,
              },
              {
                label: "Low confidence",
                value: `${scanQuality.low_confidence_pct}%`,
                hint: `${scanQuality.low_confidence} scans had ≥1 LOW-confidence field`,
                testid: "sq-low-conf",
                amber: scanQuality.low_confidence_pct >= 30,
                rose: scanQuality.low_confidence_pct >= 50,
              },
              {
                label: "Medium only",
                value: scanQuality.medium_confidence_only,
                hint: "scans flagged medium but no low-confidence fields",
                testid: "sq-medium",
              },
            ].map((m) => (
              <div
                key={m.label}
                data-testid={`admin-${m.testid}`}
                className={`bg-white p-3 ${
                  m.rose
                    ? "border-l-4 border-l-rose-400"
                    : m.amber
                      ? "border-l-4 border-l-amber-400"
                      : ""
                }`}
              >
                <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                  {m.label}
                </div>
                <div className="mt-1 text-xl font-extrabold tabular-nums text-slate-900">
                  {m.value}
                </div>
                {m.hint && (
                  <div className="mt-1 text-[10px] text-slate-500">
                    {m.hint}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-6 grid gap-px bg-slate-200 sm:grid-cols-3">
        {Object.entries(stats.plan_breakdown).map(([plan, count]) => (
          <div
            key={plan}
            data-testid={`admin-plan-${plan}`}
            className="bg-white p-4"
          >
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-black">
              {plan} plan
            </div>
            <div className="kpi-stat mt-1 font-heading text-2xl font-semibold">
              {count}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-12 flex items-center justify-between">
        <h2 className="font-heading text-xl font-medium">All users</h2>
        <div className="flex items-center gap-2">
          <Button
            data-testid="admin-open-brochure-btn"
            onClick={async () => {
              const t = toast.loading("Generating PDF…");
              try {
                await openMarketingBrochure();
                toast.success("Marketing Brochure downloaded", { id: t });
              } catch (e) {
                console.error(e);
                toast.error("Failed to generate PDF", { id: t });
              }
            }}
            variant="outline"
            size="sm"
            className="rounded-sm border-blue-300 bg-blue-50 text-blue-700 hover:bg-blue-100"
          >
            <Megaphone className="mr-2 h-4 w-4" />
            Marketing Brochure
          </Button>
          <Button
            data-testid="admin-open-manual-btn"
            onClick={async () => {
              const t = toast.loading("Generating PDF…");
              try {
                await openAdminManual();
                toast.success("Admin Manual downloaded", { id: t });
              } catch (e) {
                console.error(e);
                toast.error("Failed to generate PDF", { id: t });
              }
            }}
            variant="outline"
            size="sm"
            className="rounded-sm border-slate-300"
          >
            <BookOpen className="mr-2 h-4 w-4" />
            Admin Manual
          </Button>
          <Button
            data-testid="admin-add-user-btn"
            onClick={() => setAddOpen(true)}
            className="rounded-sm border border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
            size="sm"
          >
            <UserPlus className="mr-2 h-4 w-4" />
            Add New User
          </Button>
        </div>
      </div>

      <div className="mt-4 border border-slate-200">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="text-xs font-bold uppercase tracking-wider text-black">
                User
              </TableHead>
              <TableHead className="text-xs font-bold uppercase tracking-wider text-black">
                Role
              </TableHead>
              <TableHead className="text-xs font-bold uppercase tracking-wider text-black">
                Plan
              </TableHead>
              <TableHead className="text-xs font-bold uppercase tracking-wider text-black">
                Jobs
              </TableHead>
              <TableHead className="text-xs font-bold uppercase tracking-wider text-black">
                Resumes
              </TableHead>
              <TableHead className="text-xs font-bold uppercase tracking-wider text-black">
                Joined
              </TableHead>
              <TableHead className="w-[60px] text-right text-xs font-bold uppercase tracking-wider text-black">
                Actions
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.map((u) => {
              const isSelf = u.id === user.id;
              return (
                <TableRow key={u.id} data-testid={`admin-user-row-${u.id}`}>
                  <TableCell>
                    <div className="font-medium text-black">{u.name}</div>
                    <div className="text-xs text-black">{u.email}</div>
                    {u.company && (
                      <div
                        data-testid={`admin-user-company-${u.id}`}
                        className="text-xs text-black"
                      >
                        {u.company}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    <span
                      className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.2em] text-black ${
                        u.role === "admin" ? "bg-blue-50" : "bg-slate-50"
                      }`}
                    >
                      {u.role || "employer"}
                    </span>
                  </TableCell>
                  <TableCell className="text-sm text-black">
                    {u.subscription_plan || (
                      <span className="text-black">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-sm text-black">{u.jobs_count}</TableCell>
                  <TableCell className="text-sm text-black">{u.resumes_count}</TableCell>
                  <TableCell className="text-sm text-black">
                    {u.created_at
                      ? new Date(u.created_at).toLocaleDateString()
                      : "—"}
                  </TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          data-testid={`admin-user-actions-${u.id}`}
                          aria-label="Actions"
                          className="h-8 w-8"
                        >
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-48">
                        <DropdownMenuItem
                          data-testid={`admin-impersonate-${u.id}`}
                          disabled={isSelf}
                          onClick={() => handleImpersonate(u)}
                        >
                          <LogIn className="mr-2 h-4 w-4" />
                          Impersonate
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          data-testid={`admin-edit-${u.id}`}
                          onClick={() => setEditTarget(u)}
                        >
                          <Pencil className="mr-2 h-4 w-4" />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          data-testid={`admin-delete-${u.id}`}
                          disabled={isSelf}
                          className="text-red-600 focus:bg-red-50 focus:text-red-700"
                          onClick={() => setDeleteTarget(u)}
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      <AddUserDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        onCreated={load}
      />

      <EditUserDialog
        user={editTarget}
        open={Boolean(editTarget)}
        onOpenChange={(v) => !v && setEditTarget(null)}
        onSaved={load}
      />

      <AlertDialog
        open={Boolean(deleteTarget)}
        onOpenChange={(v) => !v && setDeleteTarget(null)}
      >
        <AlertDialogContent data-testid="admin-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete user?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget && (
                <>
                  This will permanently delete{" "}
                  <span className="font-mono">{deleteTarget.email}</span> and
                  all of their jobs, resumes, and interview records. This
                  action cannot be undone.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="admin-delete-cancel">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="admin-delete-confirm-btn"
              disabled={deleting}
              onClick={handleDelete}
              className="bg-red-600 text-white hover:bg-red-700"
            >
              {deleting ? "Deleting…" : "Delete user"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </main>
  );
}
